// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Measures the rack across one support — the step that turns geometry into numbers. The work goes: conduits
 * forming a rack here → their frame → this support measured in that frame → the conduit running through it
 * → rows → each row measured against the support's two faces.
 *
 * Any step failing returns `null` and that support is not dimensioned. Each support sees only the straight
 * part of the run passing through it, so a bent rack needs no special handling here.
 */

import {
  findParallelGroups,
  groupPipesIntoTiers,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/groupRackElements';
import {
  buildRackFrame,
  getPipeStationRange,
  projectPipesIntoRackFrame,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/rackFrame';
import {
  isSpanningSupport,
  measureSupport,
  pipesCrossingSupport,
  pipesSpannedBy,
  type RackSupportBounds,
  supportServesTier,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/crossSectionPreprocessing/rackSupportBounds';
import type {
  ProjectedRackPipe,
  RackFrame,
  RackPipe,
  RackTier,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/types';
import type { AutodeskFragmentList } from 'components/Autodesk/extensions/viewerHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { isNil, isNotNil } from 'helpers/isNotNil';

/** Vertical gap between a row's conduits and the dimension line drawn below it. */
const DIMENSION_LINE_GAP_FT = 0.34;
/** Minimum vertical gap between the dimension lines of 2 stacked rows. */
const MIN_TIER_DIMENSION_SEPARATION_FT = 0.2;
/** The shortest end span a chain may report: a face this close to a centerline *is* the conduit. */
const MIN_END_SPAN_FT = 0.25 / 12;
/** How many of a support's candidate racks to measure before taking the best. */
const MAX_CANDIDATE_RACKS = 3;

/**
 * The chained dimensions across a row in feet, left to right: left reference edge → first conduit center,
 * then each center-to-center gap, then last conduit center → right reference edge.
 */
const buildDimensionChainFt = (pipes: ProjectedRackPipe[], minWidthFt: number, maxWidthFt: number): number[] => {
  const firstPipe = pipes[0];
  const lastPipe = pipes[pipes.length - 1];
  if (isNil(firstPipe) || isNil(lastPipe)) return [];

  const centerToCenterFt = pipes.slice(1).map((pipe, index) => pipe.widthFt - (pipes[index]?.widthFt ?? pipe.widthFt));

  return [firstPipe.widthFt - minWidthFt, ...centerToCenterFt, maxWidthFt - lastPipe.widthFt];
};

/**
 * Measure one row against the support carrying it, or `null` when the support does not serve the row, does
 * not reach across 2 of its conduits, or offers no reference edge outside its outermost centerlines.
 */
const measureTier = (
  { referenceHeightFt, pipes }: { referenceHeightFt: number; pipes: ProjectedRackPipe[] },
  support: RackSupportBounds,
): RackTier | null => {
  if (!supportServesTier(support, referenceHeightFt)) return null;

  // The chain closes on the support's 2 edges, so a conduit beyond its end has no edge beside it.
  const spannedPipes = pipesSpannedBy(support, pipes);
  if (spannedPipes.length < 2) return null;

  const chainFt = buildDimensionChainFt(spannedPipes, support.minWidthFt, support.maxWidthFt);
  // Both ends have to be real edges: at zero, the support face and the conduit centerline are one line.
  const endSpansFt = [chainFt[0], chainFt[chainFt.length - 1]];
  if (endSpansFt.some((spanFt) => isNil(spanFt) || spanFt < MIN_END_SPAN_FT)) return null;

  return {
    pipes: spannedPipes,
    referenceHeightFt,
    minWidthFt: support.minWidthFt,
    maxWidthFt: support.maxWidthFt,
    dimensionLineHeightFt: referenceHeightFt - DIMENSION_LINE_GAP_FT,
    chainFt,
  };
};

/** Push each row's dimension line up so 2 closely stacked rows don't overlap. Rows arrive bottom to top. */
const separateTierDimensionLines = (tiers: RackTier[]): RackTier[] =>
  tiers.reduce<RackTier[]>((separated, tier) => {
    const previous = separated[separated.length - 1];
    const minimumHeightFt = isNil(previous)
      ? -Infinity
      : previous.dimensionLineHeightFt + MIN_TIER_DIMENSION_SEPARATION_FT;
    return [...separated, { ...tier, dimensionLineHeightFt: Math.max(tier.dimensionLineHeightFt, minimumHeightFt) }];
  }, []);

/**
 * The station the chain is drawn at: the support's outward face — whichever of its 2 faces looks away from
 * the middle of the run, so the chain sits clear of the conduits rather than buried inside them.
 */
const resolveDimensionStation = (support: RackSupportBounds, pipes: ProjectedRackPipe[]): number => {
  const { minFt, maxFt } = getPipeStationRange(pipes);
  const supportCenterFt = (support.minStationFt + support.maxStationFt) / 2;
  return supportCenterFt > (minFt + maxFt) / 2 ? support.maxStationFt : support.minStationFt;
};

/**
 * A rack measured across one support — the unit a dimension chain is drawn for. A run carries as many of
 * these as it has supports worth showing, so one conduit appears in several cross-sections.
 */
export type MeasuredCrossSection = {
  /** The support the rack was measured across. */
  supportDbId: AutodeskDbId;
  frame: RackFrame;
  /** The station the whole dimension chain is drawn at — the outward face of its support. */
  dimensionStationFt: number;
  /** The rows this support carries, ordered bottom to top. */
  tiers: RackTier[];
};

/** Measure one candidate rack across the support, or `null` when this is not the rack it carries. */
const measureRackAcrossSupport = (
  instanceTree: Autodesk.Viewing.InstanceTree,
  fragList: AutodeskFragmentList,
  supportDbId: AutodeskDbId,
  rackPipes: RackPipe[],
): MeasuredCrossSection | null => {
  const frame = buildRackFrame(rackPipes);
  if (isNil(frame)) return null;

  const projectedPipes = projectPipesIntoRackFrame(rackPipes, frame);
  const support = measureSupport(instanceTree, fragList, supportDbId, frame);
  if (isNil(support)) return null;

  // Everything past here measures only what this support holds up.
  const carriedPipes = pipesCrossingSupport(support, projectedPipes);
  if (!isSpanningSupport(support, carriedPipes)) return null;

  const tiers = groupPipesIntoTiers(carriedPipes)
    .map((tier) => measureTier(tier, support))
    .filter(isNotNil);
  if (tiers.length === 0) return null;

  return {
    supportDbId,
    frame,
    dimensionStationFt: resolveDimensionStation(support, carriedPipes),
    tiers: separateTierDimensionLines(tiers),
  };
};

/** How much of a rack a cross-section measured, used to pick between candidates. */
const measuredConduitCount = ({ tiers }: MeasuredCrossSection): number =>
  tiers.reduce((total, { pipes }) => total + pipes.length, 0);

/**
 * Measure the rack across one support, or `null` when nothing there can be dimensioned. Every candidate
 * rack in the support's neighborhood is measured and the fullest result wins — see
 * {@link findParallelGroups} for why the biggest group is not always the right one.
 */
export const buildMeasuredCrossSection = (
  instanceTree: Autodesk.Viewing.InstanceTree,
  fragList: AutodeskFragmentList,
  supportDbId: AutodeskDbId,
  /** Every conduit found near the support; narrowed here to the ones it carries */
  candidatePipes: RackPipe[],
): MeasuredCrossSection | null =>
  findParallelGroups(candidatePipes)
    .slice(0, MAX_CANDIDATE_RACKS)
    .map((rackPipes) => measureRackAcrossSupport(instanceTree, fragList, supportDbId, rackPipes))
    .filter(isNotNil)
    .toSorted((a, b) => measuredConduitCount(b) - measuredConduitCount(a))[0] ?? null;
